import tailwindConfig from "../../tailwind.config.js";
import resolveConfig from "tailwindcss/resolveConfig";
import {
  Action,
  SettingParam,
  SettingParamTypes,
  WorkflowNodeTypes,
} from "@/types/node";
import {WorkflowNodeSetting} from "@/types/node";
import {Node, Edge, Position} from "@xyflow/react";
import {PromptBuilder} from "../lib/promptBuilder";
export type Direction = "TB" | "LR" | "RL" | "BT";

export function capitalize(str: string): string {
  if (!str) return "";
  return str
    .toLowerCase()
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function generateId() {
  return (
    new Date().getTime().toString(36) + Math.random().toString(36).slice(2)
  );
}

export const getTailwindColor = (colorClass: string) => {
  if (!colorClass) return "";
  const fullConfig = resolveConfig(tailwindConfig);
  const tailwindColors = fullConfig.theme.colors;
  // Convert Tailwind class name to actual color value
  // Make this more function more dynamic to support more prefixes
  const [colorName, colorValue] = colorClass.replace("bg-", "").split("-");
  return (
    tailwindColors[colorName][colorValue] ||
    tailwindColors[colorName] ||
    colorClass
  );
};

export const getQueryParams = () => {
  return new URLSearchParams(window.location.search);
};

export const findPromptValues = nodes => {
  const result = [];

  const extractValues = obj => {
    if (typeof obj !== "object" || obj === null) return;

    Object.values(obj).forEach(value => {
      if (Array.isArray(value)) {
        value.forEach(item => extractValues(item));
      } else if (typeof value === "object") {
        extractValues(value);
      }
    });

    if (
      obj.type === SettingParamTypes.PROMPT &&
      obj.value &&
      obj.value.length > 0
    ) {
      result.push(obj.value);
    }
  };

  nodes.forEach(node => {
    if (node.data?.settings) {
      extractValues(node.data.settings);
    }
  });

  return result;
};

export const getSettingValueByKey = (
  settings: WorkflowNodeSetting[],
  keyName: string,
): SettingParam["value"] => {
  if (!settings) return null;
  for (const setting of settings) {
    if (setting.params && Array.isArray(setting.params)) {
      const param = setting.params.find((p: SettingParam) => p.key === keyName);
      if (param) {
        if (typeof param.value === "string") {
          return param.value.trim();
        }

        return param.value;
      }
    }
  }
  return null;
};

export const getSettingValueByKeys = (
  isNodeType: boolean,
  settings: any[],
  keys: string[],
) => {
  return isNodeType
    ? settings?.filter(setting => keys.includes(setting.params?.[0]?.key))
    : [];
};

export const getNodesByType = (nodes: Node[], type: WorkflowNodeTypes) => {
  return nodes.filter(node => node.type === type) || [];
};

export const getFlowNodeSetting = (node: Node, key: string) => {
  return node.data.settings[key] || null;
};

export const getNodeElement = (nodeId: string) => {
  return window.__flowBuilderIframeDoc.querySelector(
    `[data-id="${nodeId}"]`,
  ) as HTMLElement;
};

export const getAllActionIds = nodes => {
  const actionIds = new Set(
    nodes.flatMap(
      node =>
        node.data?.settings?.flatMap(setting =>
          setting.params
            ?.filter(
              param =>
                param.type === SettingParamTypes.ACTIONS &&
                Array.isArray(param.value),
            )
            .flatMap(param => param.value.map(action => action.action_id)),
        ) || [],
    ),
  );
  return Array.from(actionIds);
};

export const getAllActions = (nodes): Action[] => {
  const actions = Array.from(
    new Set<Action>(
      nodes.flatMap(
        node =>
          node.data?.settings?.flatMap(
            setting =>
              setting.params?.flatMap(param =>
                param.type === SettingParamTypes.ACTIONS &&
                Array.isArray(param.value)
                  ? param.value
                  : [],
              ) ?? [],
          ) ?? [],
      ),
    ),
  );

  return actions;
};

export function getSourceHandlePosition(direction: Direction) {
  switch (direction) {
    case "TB":
      return Position.Bottom;
    case "BT":
      return Position.Top;
    case "LR":
      return Position.Right;
    case "RL":
      return Position.Left;
  }
}

export function getTargetHandlePosition(direction: Direction) {
  switch (direction) {
    case "TB":
      return Position.Top;
    case "BT":
      return Position.Bottom;
    case "LR":
      return Position.Left;
    case "RL":
      return Position.Right;
  }
}

function getParams(nodeA, nodeB) {
  const centerA = getNodeCenter(nodeA);
  const centerB = getNodeCenter(nodeB);

  const horizontalDiff = Math.abs(centerA.x - centerB.x);
  const verticalDiff = Math.abs(centerA.y - centerB.y);

  // when the horizontal difference between the nodes is bigger, we use Position.Left or Position.Right for the handle
  const position = centerA.x > centerB.x ? Position.Left : Position.Right;
  // if (horizontalDiff > verticalDiff) {
  // } else {
  //   // here the vertical difference between the nodes is bigger, so we use Position.Top or Position.Bottom for the handle
  //   position = centerA.y > centerB.y ? Position.Top : Position.Bottom;
  // }

  const [x, y] = getHandleCoordsByPosition(nodeA, position);
  return [x, y, position];
}

function getHandleCoordsByPosition(node, handlePosition) {
  // all handles are from type source, that's why we use handleBounds.source here
  const handle = node.internals.handleBounds.source.find(
    h => h.position === handlePosition,
  );

  let offsetX = handle.width / 2;
  let offsetY = handle.height / 2;

  // this is a tiny detail to make the markerEnd of an edge visible.
  // The handle position that gets calculated has the origin top-left, so depending which side we are using, we add a little offset
  // when the handlePosition is Position.Right for example, we need to add an offset as big as the handle itself in order to get the correct position
  switch (handlePosition) {
    case Position.Left:
      offsetX = 0;
      break;
    case Position.Right:
      offsetX = handle.width;
      break;
    case Position.Top:
      offsetY = 0;
      break;
    case Position.Bottom:
      offsetY = handle.height;
      break;
  }

  const x = node.internals.positionAbsolute.x + handle.x + offsetX;
  const y = node.internals.positionAbsolute.y + handle.y + offsetY;

  return [x, y];
}

function getNodeCenter(node) {
  return {
    x: node.internals.positionAbsolute.x + node.measured.width / 2,
    y: node.internals.positionAbsolute.y + node.measured.height / 2,
  };
}

// returns the parameters (sx, sy, tx, ty, sourcePos, targetPos) you need to create an edge
export function getEdgeParams(source, target) {
  const [sx, sy, sourcePos] = getParams(source, target);
  const [tx, ty, targetPos] = getParams(target, source);

  return {
    sx,
    sy,
    tx,
    ty,
    sourcePos,
    targetPos,
  };
}

export const extractBracketedSections = (strings, [open, close]) => {
  const escapedOpen = open.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
  const escapedClose = close.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");

  const regex = new RegExp(
    `${escapedOpen}([^${escapedClose}]+)${escapedClose}`,
    "g",
  );
  const uniqueSections = new Set();

  strings.forEach(str => {
    let match;
    while ((match = regex.exec(str)) !== null) {
      uniqueSections.add(`${open}${match[1]}${close}`.replace(/\u200B/g, ""));
    }
  });

  return Array.from(uniqueSections);
};

const cleanUpPrompt = (text: string): string => {
  return text.replace(/\n{3,}/g, "\n\n"); // Replace 3 or more new lines with exactly 2
};

export const generatePrompt = (nodes: Node[], edges: Edge[]) => {
  const builder = new PromptBuilder(nodes as Node[], edges as Edge[]);
  let markdown = builder.buildMarkdown();
  markdown = cleanUpPrompt(markdown);

  return markdown;
};

export const extractHashtagSections = strings => {
  const regex = /#(\S+)(?=\s|$)/g;
  const uniqueHashtags = new Set();

  strings.forEach(str => {
    let match;
    while ((match = regex.exec(str)) !== null) {
      uniqueHashtags.add(`#${match[1]}`.replace(/\u200B/g, ""));
    }
  });

  return Array.from(uniqueHashtags);
};

export const truncatePath = (path: string) => {
  const commands = path.split(/[A-Z]/).filter(Boolean);
  const commandLetters = path.match(/[A-Z]/g);

  if (!commands || !commandLetters) return path;

  const midpoint = Math.floor(commands.length / 2);

  const truncatedCommands = commands.slice(0, midpoint);
  const truncatedPath = truncatedCommands
    .map((cmd, index) => commandLetters[index] + cmd.trim())
    .join(" ");

  return truncatedPath;
};

export const isEmptySettingValue = (value: any): boolean => {
  if (value === undefined || value === null) return true; // Check null/undefined
  if (typeof value === "string" && value.trim() === "") return true; // Check empty string
  if (typeof value === "number") return false; // Numbers are always valid
  if (typeof value === "boolean") return false; // Booleans are always valid
  if (Array.isArray(value)) return value.length === 0; // Check empty array
  if (typeof value === "object") return Object.keys(value).length === 0; // Check empty object

  return false;
};
